import React from 'react';
import { Button, Flex, FlexItem, TextControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import logo from '../assets/logo.png';

interface CalculatorCreationProps {
  localCalcId: string;
  onCalcIdChange: (newValue: string) => void;
  onSelectTemplate: () => void;
  onEditCalculator: () => void;
  onCreateCalculator: () => void;
  onSelectCustomCalculators: () => void;
  onEmbedCalculator: () => void;
  errorMessage?: string;
}

export default function CalculatorCreation({
  localCalcId,
  onCalcIdChange,
  onSelectTemplate,
  onEditCalculator,
  onCreateCalculator,
  onSelectCustomCalculators,
  onEmbedCalculator,
  errorMessage,
}: CalculatorCreationProps): JSX.Element {
  return (
    <div className="calculator-creation">
      <div className="calculator-header">
        <img src={logo} alt="Logo" className="calculator-logo" />

        <p className="calculator-title">
          {__('Embed Calculator', 'convertcalculator')}
        </p>
      </div>

      <Flex direction="column">
        <FlexItem>
          <span>
            {__(
              'Get a headstart by using our extensive templates, or start building any calculator, quote form or lead funnel from scratch.',
              'convertcalculator',
            )}
          </span>
        </FlexItem>
        <Flex direction="row" className="button-group">
          <FlexItem>
            <Button
              variant="primary"
              disabled={localCalcId.length > 5 && !!errorMessage}
              onClick={localCalcId.length > 5 ? onEditCalculator : onSelectTemplate}
            >
              {localCalcId.length > 5
                ? __('Edit Calculator', 'convertcalculator')
                : __('Pick a Template', 'convertcalculator')}
            </Button>
          </FlexItem>
          <FlexItem>
            <Button variant="secondary" onClick={onCreateCalculator}>
              {__('Start from scratch', 'convertcalculator')}
            </Button>
          </FlexItem>
        </Flex>
      </Flex>

      <hr className="separator" />

      <Flex direction="column" className="existing-calculator">
        <FlexItem>
          <span>{__('Already created a calculator?', 'convertcalculator')}</span>
        </FlexItem>
        <Flex direction="row" className="custom-calculator-group">
          <FlexItem>
            <Button variant="secondary" onClick={onSelectCustomCalculators}>
              {__('Select calculator', 'convertcalculator')}
            </Button>
          </FlexItem>
        </Flex>
      </Flex>

      <p className="calculator-id-prompt">
        {__('Or paste in your Calculator ID', 'convertcalculator')}
      </p>

      <div className="calculator-id-container">
        <Flex align="baseline" className="calculator-id-input-group">
          <FlexItem isBlock>
            <TextControl
              className="calculator-id-input"
              value={localCalcId}
              onChange={onCalcIdChange}
              placeholder={__('Paste in your Calculator ID', 'convertcalculator')}
              maxLength={17}
            />
          </FlexItem>
          <FlexItem>
            <Button
              variant="primary"
              onClick={onEmbedCalculator}
              disabled={!!errorMessage}
            >
              {__('Embed', 'convertcalculator')}
            </Button>
          </FlexItem>
        </Flex>

        <div className="error-message-container">
          {errorMessage && <p className="error-message">{errorMessage}</p>}
        </div>
      </div>

      <p className="learn-more-container">
        <a
          href="https://www.convertcalculator.com/help/embedding/embedding-your-calculator-on-a-wordpress-website/"
          target="_blank"
          rel="noopener noreferrer"
          className="learn-more-link"
        >
          {__('Learn more about embedding ↗', 'convertcalculator')}
        </a>
      </p>
    </div>
  );
}
